import { useState } from "react";
import { CreditCard, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const input = "w-full rounded-lg border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none focus:border-amber-deep";

export const chequeContactOk = (user, c) => {
  const src = user || c;
  const addrOk = Boolean(src?.address_line?.trim() && src?.city?.trim() && /^\d{5}$/.test((src?.postal_code || "").trim()));
  if (user) return addrOk;
  return addrOk && c.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email.trim()) && c.phone.replace(/\D/g, "").length >= 10;
};

function GuestFields({ contact, setContact }) {
  const set = (k) => (e) => setContact({ ...contact, [k]: e.target.value });
  return (
    <div data-testid="cheque-guest-fields" className="mt-3 grid grid-cols-2 gap-2">
      <input required aria-required="true" data-testid="cheque-name" placeholder="Nom et prénom *" value={contact.name} onChange={set("name")} className={`${input} col-span-2`} />
      <input required aria-required="true" data-testid="cheque-email" type="email" placeholder="E-mail *" value={contact.email} onChange={set("email")} className={`${input} col-span-2`} />
      <input required aria-required="true" data-testid="cheque-phone" type="tel" placeholder="Téléphone *" value={contact.phone} onChange={set("phone")} className={`${input} col-span-2`} />
      <input required aria-required="true" data-testid="cheque-address" placeholder="Adresse *" value={contact.address_line} onChange={set("address_line")} className={`${input} col-span-2`} />
      <input required aria-required="true" data-testid="cheque-postal" inputMode="numeric" maxLength={5} placeholder="Code postal *" value={contact.postal_code} onChange={(e) => setContact({ ...contact, postal_code: e.target.value.replace(/\D/g, "") })} className={input} />
      <input required aria-required="true" data-testid="cheque-city" placeholder="Ville *" value={contact.city} onChange={set("city")} className={input} />
      <p data-testid="cheque-required-hint" className="col-span-2 text-xs text-stone-mute">* Tous les champs sont obligatoires. Pays : France (le chèque est réservé aux résidents en France).</p>
    </div>
  );
}

function AccountAddress({ user }) {
  const { refreshUser } = useAuth();
  const [a, setA] = useState({ phone: user.phone || "", address_line: user.address_line || "", postal_code: user.postal_code || "", city: user.city || "" });
  const [busy, setBusy] = useState(false);
  if (chequeContactOk(user)) {
    return <p data-testid="cheque-account-address" className="mt-2 text-xs text-bark-soft">Adresse en France : {user.address_line}, {user.postal_code} {user.city}</p>;
  }
  const set = (k) => (e) => setA({ ...a, [k]: k === "postal_code" ? e.target.value.replace(/\D/g, "") : e.target.value });
  const save = async () => {
    if (!chequeContactOk(null, { ...a, name: user.name || "", email: user.email || "" })) return toast.error("Renseignez téléphone, adresse, code postal (5 chiffres) et ville");
    setBusy(true);
    try {
      await api.put("/customer/profile", { name: user.name, ...a });
      await refreshUser();
      toast.success("Adresse enregistrée dans votre compte");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="cheque-account-missing" className="mt-3 grid grid-cols-2 gap-2">
      <p className="col-span-2 text-xs font-medium text-terra">Le chèque est réservé aux résidents en France : indiquez votre adresse (elle sera enregistrée dans votre compte).</p>
      <input data-testid="cheque-inline-phone" type="tel" placeholder="Téléphone *" value={a.phone} onChange={set("phone")} className={`${input} col-span-2`} />
      <input data-testid="cheque-inline-address" placeholder="Adresse *" value={a.address_line} onChange={set("address_line")} className={`${input} col-span-2`} />
      <input data-testid="cheque-inline-postal" inputMode="numeric" maxLength={5} placeholder="Code postal *" value={a.postal_code} onChange={set("postal_code")} className={input} />
      <input data-testid="cheque-inline-city" placeholder="Ville *" value={a.city} onChange={set("city")} className={input} />
      <button type="button" data-testid="cheque-inline-save" disabled={busy} onClick={save} className="col-span-2 flex items-center justify-center gap-2 rounded-full bg-amber-deep py-2 text-xs font-semibold text-white hover:bg-honey disabled:opacity-60">
        {busy && <Loader2 size={13} className="animate-spin" />} Enregistrer mon adresse
      </button>
    </div>
  );
}

export default function PaymentMethodPicker({ settings, method, setMethod, user, contact, setContact, onNavigate }) {
  const option = (id, Icon, label) => (
    <label className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${method === id ? "border-amber-deep bg-amber-50" : "border-amber-900/15 bg-white"}`}>
      <input type="radio" name="payment-method" data-testid={`payment-method-${id}`} checked={method === id} onChange={() => setMethod(id)} className="accent-amber-deep" />
      <Icon size={16} className="text-amber-deep" />
      <span className="text-sm font-medium text-bark">{label}</span>
    </label>
  );
  return (
    <div data-testid="payment-methods" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-stone-mute">Paiement</p>
      {option("card", CreditCard, "PayPal ou carte bancaire")}
      {option("cheque", FileText, "Chèque (résidents en France)")}
      {method === "cheque" && (
        <div data-testid="cheque-panel" className="rounded-xl border border-amber-800/15 bg-amber-50/60 px-4 py-3">
          <p data-testid="cheque-message" className="text-sm font-semibold text-bark">{settings.message}</p>
          <p className="mt-1 text-xs text-bark-soft">À l'ordre de <strong>{settings.payee}</strong>. L'adresse d'envoi vous sera indiquée après validation.</p>
          {user ? <AccountAddress user={user} onNavigate={onNavigate} /> : <GuestFields contact={contact} setContact={setContact} />}
        </div>
      )}
    </div>
  );
}
